// src/pages/MyChatbots.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Paper,
    Divider,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
// Make sure this path is correct for your project structure
import api from '../utils/api'; 
import { useChatbots } from '../contexts/ChatbotContext';

export default function MyChatbots() {
    // Consume the global chatbot state and functions from the context
    const { chatbots, activeChatbot, createNewChatbot, switchActiveChatbot, refreshChatbots } = useChatbots();
    const navigate = useNavigate();
    
    const handleSetDefault = async (chatbotId) => {
        if (window.confirm("Are you sure you want to make this your default chatbot?")) {
            try {
                // This endpoint needs to exist on your backend
                await api.put(`/chatbots/${chatbotId}/set-default`);
                alert("Default chatbot updated!");
                refreshChatbots(); // Refresh the context to get the new `is_default` flags
            } catch (error) {
                alert("Failed to update default chatbot.");
                console.error(error);
            }
        }
    };
    
    // Function to navigate to the setup steps for an incomplete bot
    const handleContinueSetup = () => {
        navigate(`/chatbot/create`);
    };

    return (
        <>
            <Paper sx={{ p: 3, mt: 10, maxWidth: '900px', mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" component="h1">
                        My Chatbots
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddCircleIcon />}
                        onClick={handleContinueSetup}
                    >
                        Create New Chatbot
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Here you can manage all of your chatbots. The default chatbot (marked with a <StarIcon fontSize="small" sx={{ verticalAlign: 'middle' }}/>) will be selected automatically when you log in.
                </Typography>
                <List>
                    {chatbots.map((bot) => (
                        <ListItem
                            key={bot.id}
                            secondaryAction={
                                <Box>
                                     <Tooltip title={bot.is_default ? "This is your default chatbot" : "Make default"}>
                                        <span>
                                            <IconButton
                                                edge="end"
                                                aria-label="set-default"
                                                onClick={() => handleSetDefault(bot.id)}
                                                disabled={bot.is_default}
                                            >
                                                {bot.is_default ? <StarIcon color="primary" /> : <StarBorderIcon />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            }
                            sx={{
                                border: bot.id === activeChatbot?.id ? '2px solid' : '1px solid',
                                borderColor: bot.id === activeChatbot?.id ? 'primary.main' : 'divider',
                                borderRadius: 2,
                                mb: 1.5,
                                bgcolor: 'background.paper'
                            }}
                        >
                            <ListItemIcon>
                                <SmartToyIcon color={bot.id === activeChatbot?.id ? 'primary' : 'inherit'} />
                            </ListItemIcon>
                            <ListItemText
                                primary={bot.chatbot_name}
                                secondary={
                                    bot.is_process_completed
                                        ? "Status: Ready"
                                        : "Status: Setup incomplete"
                                }
                            />
                            {bot.id !== activeChatbot?.id && (
                                <Button variant="outlined" size="small" sx={{ mr: 2 }} onClick={() => switchActiveChatbot(bot.id, { navigateToDashboard : true })}>
                                    Switch To This Bot
                                </Button>
                            )}
                             {!bot.is_process_completed && (
                                <Button variant="contained" color="warning" size="small" onClick={() => handleContinueSetup(bot.id)}>
                                    Continue Setup
                                </Button>
                            )}
                        </ListItem>
                    ))}
                </List>
            </Paper>
        </>
    );
}